import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import { type FC, memo, useEffect, useRef } from 'react';
import type { Tagged } from 'type-fest';
import styles from './VideoThumbnail.module.sass';
import VideoThumbnailInner from './VideoThumbnailInner';

export type ThumbnailLevel = Tagged<1 | 2 | 3, 'ThumbnailLevel'>;
export const THUMBNAIL_LEVEL_SEEK = 1 as ThumbnailLevel;
export const THUMBNAIL_LEVEL_AI = 2 as ThumbnailLevel;
export const THUMBNAIL_LEVEL_AD = 3 as ThumbnailLevel;

export interface VideoThumbnailProps {
  /** Whether the video player is enabled. */
  enabled?: boolean;
  /** The camera to play video from. */
  playingCamera: PlayingCamera;
  /** The current time where the video is played. */
  playHeadTime: number;
  /** Limit playback to specific level */
  onlyLevelEnabled?: ThumbnailLevel;
  /** Callback to set the video element reference. */
  setVideoRef?: (el: HTMLVideoElement | null) => void;
  /** Time zone identifier for displaying time-related information. */
  timeZoneId: string;
}

const VideoThumbnail: FC<VideoThumbnailProps> = ({
  enabled = true,
  playingCamera,
  playHeadTime,
  onlyLevelEnabled,
  setVideoRef,
  timeZoneId,
}) => {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    setVideoRef?.(enabled ? ref.current : null);
  }, [enabled, setVideoRef]);

  if (onlyLevelEnabled) {
    return (
      <VideoThumbnailInner
        thumbnailLevel={onlyLevelEnabled}
        playHeadTime={playHeadTime}
        playingCamera={playingCamera}
        setVideoRef={(el) => (ref.current = el)}
        timeZoneId={timeZoneId}
      />
    );
  }

  return (
    <div className={styles.thumbnail} style={{ opacity: enabled ? '1' : '0' }}>
      <VideoThumbnailInner
        thumbnailLevel={THUMBNAIL_LEVEL_SEEK}
        playHeadTime={playHeadTime}
        playingCamera={playingCamera}
        timeZoneId={timeZoneId}
      />
      <VideoThumbnailInner
        thumbnailLevel={THUMBNAIL_LEVEL_AI}
        // Larger delay since level 2 could be running at 1FPS.
        debounceDelay={250}
        playHeadTime={playHeadTime}
        playingCamera={playingCamera}
        setVideoRef={(el) => (ref.current = el)}
        timeZoneId={timeZoneId}
      />
    </div>
  );
};

export default memo(VideoThumbnail);
